import React, { useCallback, useEffect, useState } from "react";
import {
	apiErrorText,
	ChainPayoutLock,
	ChainRow,
	getChainPayoutLock,
	listChains,
	lockChainPayout,
	separateChainOwner,
	unlockChainPayout,
	updateChainProfile,
} from "../../../../services/LegalEntityService";
import { TaoChuoiMoi, ThemDiemVaoChuoi } from "./ChainCreateAddLocation";
import ChainPermissionsAdmin from "./ChainPermissionsAdmin";

/**
 * Bảng CHUỖI — mỗi dòng một chuỗi (18/10/2026, Quân: "tách bảng coi station lẻ, coi chuỗi, và station trong chuỗi").
 * Dùng CHUNG cho Station Management (chế độ xem Chains) và màn Legal entities & chains (tab Chains) — một bản code.
 * "Stations →" mở đúng danh sách trạm của chuỗi ở chế độ xem Stations (không dựng trang chi tiết riêng).
 */
type Notice = { tone: "ok" | "err"; text: string };

const cell: React.CSSProperties = { padding: "8px 10px", borderBottom: "1px solid #eaecf0", verticalAlign: "top" };
const input: React.CSSProperties = { padding: "6px 8px", border: "1px solid #d0d5dd", borderRadius: 6, fontSize: 14 };
const nut = (chinh = false): React.CSSProperties => ({
	padding: "6px 12px",
	borderRadius: 6,
	border: chinh ? "1px solid #1565c0" : "1px solid #d0d5dd",
	background: chinh ? "#1565c0" : "#fff",
	color: chinh ? "#fff" : "#344054",
	cursor: "pointer",
	fontSize: 13,
	whiteSpace: "nowrap",
});

export function tenHienThiChuoi(c: Pick<ChainRow, "id" | "name" | "nameSuggestion">): string {
	return c.name || c.nameSuggestion || `Chain #${c.id}`;
}

const ChainsTable: React.FC<{ onNotice?: (n: Notice) => void; onOpenStations?: (c: ChainRow) => void }> = ({ onNotice, onOpenStations }) => {
	const [rows, setRows] = useState<ChainRow[]>([]);
	const [names, setNames] = useState<Record<number, string>>({});
	const [loading, setLoading] = useState(false);
	const [busy, setBusy] = useState<number | null>(null);
	const [notice, setNotice] = useState<Notice | null>(null);
	/* 28/10/2026: ô tách chủ chuỗi đang mở cho chuỗi nào (một lúc một chuỗi). */
	const [tachMo, setTachMo] = useState<number | null>(null);
	const [tachEmail, setTachEmail] = useState("");
	const [tachLyDo, setTachLyDo] = useState("");
	/* 29/10/2026: ô khoá rút tiền cả chuỗi đang mở cho chuỗi nào. */
	const [khoaMo, setKhoaMo] = useState<number | null>(null);
	const [khoaTT, setKhoaTT] = useState<ChainPayoutLock | null>(null);
	const [khoaLyDo, setKhoaLyDo] = useState("");
	const bao = useCallback(
		(n: Notice) => {
			if (onNotice) onNotice(n);
			else setNotice(n);
		},
		[onNotice]
	);

	const load = useCallback(async () => {
		setLoading(true);
		try {
			const r = await listChains();
			// Chuỗi đang BẬT lên đầu, rồi nhiều trạm trước.
			r.sort((a, b) => Number(b.chainModeEnabled) - Number(a.chainModeEnabled) || b.soDiem - a.soDiem || a.id - b.id);
			setRows(r);
			setNames(Object.fromEntries(r.map((c) => [c.id, c.name || c.nameSuggestion || ""])));
		} catch (e) {
			bao({ tone: "err", text: apiErrorText(e) });
		} finally {
			setLoading(false);
		}
	}, [bao]);
	useEffect(() => {
		load();
	}, [load]);

	const save = async (c: ChainRow) => {
		setBusy(c.id);
		try {
			await updateChainProfile(c.id, { name: (names[c.id] || "").trim() || null });
			bao({ tone: "ok", text: `Chain #${c.id} renamed to “${(names[c.id] || "").trim()}”.` });
			await load();
		} catch (e) {
			bao({ tone: "err", text: apiErrorText(e) });
		} finally {
			setBusy(null);
		}
	};

	const tach = async (c: ChainRow) => {
		const email = tachEmail.trim();
		if (!email || !tachLyDo.trim()) {
			bao({ tone: "err", text: "Enter the chain owner email and a reason." });
			return;
		}
		if (
			!window.confirm(
				`Separate the chain owner of “${tenHienThiChuoi(c)}”?\n\n` +
					`• ${email} becomes the chain owner account (no bookings, no location).\n` +
					`• Station #${c.storeOwnerPartnerId}${c.ownerName ? ` ${c.ownerName}` : ""} becomes a normal station of the chain — its bookings, balance, PIN and login stay the same.`
			)
		) {
			return;
		}
		setBusy(c.id);
		try {
			const kq = await separateChainOwner(c.id, { newOwnerEmail: email, reason: tachLyDo.trim() });
			bao({
				tone: "ok",
				text:
					`Chain #${c.id}: owner is now ${kq.toEmail} (record #${kq.newOwnerPartnerId}). Station #${kq.oldOwnerPartnerId} is a normal station.` +
					(kq.emailSent ? " Sign-in email sent." : " Sign-in email could not be sent — send it again from the station portal (Forgot password)."),
			});
			setTachMo(null);
			setTachEmail("");
			setTachLyDo("");
			await load();
		} catch (e) {
			bao({ tone: "err", text: apiErrorText(e) });
		} finally {
			setBusy(null);
		}
	};

	const moKhoa = async (c: ChainRow) => {
		setKhoaMo(c.id);
		setKhoaTT(null);
		setKhoaLyDo("");
		try {
			setKhoaTT(await getChainPayoutLock(c.id));
		} catch (e) {
			bao({ tone: "err", text: apiErrorText(e) });
		}
	};

	const khoa = async (c: ChainRow) => {
		const lyDo = khoaLyDo.trim();
		if (lyDo.length < 20) {
			bao({ tone: "err", text: "Reason must be at least 20 characters — stations see it on their Payouts page." });
			return;
		}
		if (!window.confirm(`Lock payouts for every station in “${tenHienThiChuoi(c)}”?\n\nStations already locked for another reason keep their own lock.`)) return;
		setBusy(c.id);
		try {
			const kq = await lockChainPayout(c.id, lyDo);
			setKhoaTT(kq);
			bao({ tone: "ok", text: `Chain #${c.id}: payouts locked for ${kq.lockedNow.length} station(s).` });
		} catch (e) {
			bao({ tone: "err", text: apiErrorText(e) });
		} finally {
			setBusy(null);
		}
	};

	const moKhoaChuoi = async (c: ChainRow) => {
		if (!window.confirm(`Unlock payouts for “${tenHienThiChuoi(c)}”?\n\nOnly the chain lock is removed. Locks set for other reasons stay.`)) return;
		setBusy(c.id);
		try {
			const kq = await unlockChainPayout(c.id);
			setKhoaTT(kq);
			bao({ tone: "ok", text: `Chain #${c.id}: payouts unlocked for ${kq.unlockedNow.length} station(s).` });
		} catch (e) {
			bao({ tone: "err", text: apiErrorText(e) });
		} finally {
			setBusy(null);
		}
	};

	return (
		<div>
			<p style={{ color: "#555", maxWidth: 900, lineHeight: 1.6, margin: "0 0 10px" }}>
				One row per chain. A chain name covers <b>all its stations</b> (e.g. “HK Busline”). Chain mode is switched from the chain owner&apos;s
				Actions menu.
			</p>
			<TaoChuoiMoi
				onDone={(text) => {
					bao({ tone: "ok", text });
					load();
				}}
			/>
			{notice ? <p style={{ color: notice.tone === "ok" ? "#067647" : "#b42318", margin: "0 0 8px" }}>{notice.text}</p> : null}
			{loading ? <p style={{ color: "#667085" }}>Loading…</p> : null}
			<div style={{ overflowX: "auto" }}>
				<table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
					<thead>
						<tr style={{ background: "#f9fafb", textAlign: "left" }}>
							<th style={cell}>Chain</th>
							<th style={cell}>Name</th>
							<th style={cell}>Chain owner</th>
							<th style={cell}>Managing entity</th>
							<th style={cell}>Stations</th>
							<th style={cell}>Chain mode</th>
							<th style={cell}>Payouts</th>
							<th style={cell}></th>
						</tr>
					</thead>
					<tbody>
						{rows.map((c) => (
							<tr key={c.id}>
								<td style={{ ...cell, fontFamily: "monospace" }}>#{c.id}</td>
								<td style={cell}>
									<input value={names[c.id] || ""} onChange={(e) => setNames({ ...names, [c.id]: e.target.value })} style={{ ...input, minWidth: 200 }} />
									{!c.name && c.nameSuggestion ? <div style={{ color: "#b54708", fontSize: 12, marginTop: 4 }}>Suggested — not saved yet</div> : null}
								</td>
								<td style={cell}>
									{c.storeOwnerPartnerId ? (
										<div>
											<a href={`/partner?filterKeyword=${c.storeOwnerPartnerId}`}>
												#{c.storeOwnerPartnerId}
												{c.ownerName ? ` ${c.ownerName}` : ""}
											</a>
											{c.ownerEmail ? <div style={{ color: "#667085", fontSize: 12 }}>{c.ownerEmail}</div> : null}
											{c.ownerIsStation ? (
												<div style={{ marginTop: 6 }}>
													<div style={{ color: "#b54708", fontSize: 12 }}>Owner is a station — needs its own account</div>
													{tachMo === c.id ? (
														<div style={{ display: "grid", gap: 6, marginTop: 6, maxWidth: 280 }}>
															<input
																type="email"
																placeholder="Chain owner email (not a station's)"
																value={tachEmail}
																onChange={(e) => setTachEmail(e.target.value)}
																style={input}
															/>
															<input placeholder="Reason" value={tachLyDo} onChange={(e) => setTachLyDo(e.target.value)} style={input} />
															<div style={{ display: "flex", gap: 6 }}>
																<button type="button" disabled={busy === c.id} onClick={() => tach(c)} style={nut(true)}>
																	{busy === c.id ? "Separating…" : "Separate"}
																</button>
																<button type="button" onClick={() => setTachMo(null)} style={nut()}>
																	Cancel
																</button>
															</div>
														</div>
													) : (
														<button
															type="button"
															onClick={() => {
																setTachMo(c.id);
																setTachEmail("");
																setTachLyDo("");
															}}
															style={{ ...nut(), marginTop: 4 }}>
															Separate owner account
														</button>
													)}
												</div>
											) : null}
										</div>
									) : (
										<span style={{ color: "#667085" }}>—</span>
									)}
								</td>
								<td style={cell}>{c.managingEntityId ? `#${c.managingEntityId} ${c.managingEntityName || ""}` : <span style={{ color: "#667085" }}>Not set</span>}</td>
								<td style={{ ...cell, fontVariantNumeric: "tabular-nums" }}>
									{c.soDiem}
									{c.dangBan ? <span style={{ color: "#667085" }}> · {c.dangBan} published</span> : null}
									<ThemDiemVaoChuoi
										storeId={c.id}
										onDone={(text) => {
											bao({ tone: "ok", text });
											load();
										}}
									/>
								</td>
								<td style={cell}>
									<span
										style={{
											padding: "2px 8px",
											borderRadius: 10,
											fontSize: 12,
											background: c.chainModeEnabled ? "#ecfdf3" : "#f2f4f7",
											color: c.chainModeEnabled ? "#067647" : "#475467",
										}}>
										{c.chainModeEnabled ? "ON" : "OFF"}
									</span>
								</td>
								<td style={cell} data-testid={`chain-payout-${c.id}`}>
									{khoaMo === c.id ? (
										<div style={{ display: "grid", gap: 6, maxWidth: 300 }}>
											{khoaTT ? (
												<div style={{ fontSize: 13, color: "#344054" }}>
													{khoaTT.chainLocked
														? `Chain lock ON — ${khoaTT.chainLocked} of ${khoaTT.total} station(s) locked.`
														: `Chain lock OFF — ${khoaTT.total} station(s).`}
													{khoaTT.otherLocked ? (
														<div style={{ color: "#b54708", fontSize: 12 }}>{khoaTT.otherLocked} locked for another reason (not changed here).</div>
													) : null}
												</div>
											) : (
												<span style={{ color: "#667085", fontSize: 13 }}>Loading…</span>
											)}
											<ChainPermissionsAdmin storeId={c.id} onNotice={bao} />
											{khoaTT && khoaTT.chainLocked < khoaTT.total - khoaTT.otherLocked ? (
												<>
													<input
														placeholder="Reason shown to stations (min 20 characters)"
														value={khoaLyDo}
														onChange={(e) => setKhoaLyDo(e.target.value)}
														style={input}
													/>
													<button type="button" disabled={busy === c.id} onClick={() => khoa(c)} style={nut(true)}>
														{busy === c.id ? "Locking…" : "Lock payouts for whole chain"}
													</button>
												</>
											) : null}
											{khoaTT && khoaTT.chainLocked ? (
												<button type="button" disabled={busy === c.id} onClick={() => moKhoaChuoi(c)} style={nut(true)}>
													{busy === c.id ? "Unlocking…" : "Unlock chain"}
												</button>
											) : null}
											<button type="button" onClick={() => setKhoaMo(null)} style={nut()}>
												Close
											</button>
										</div>
									) : (
										<button type="button" onClick={() => moKhoa(c)} style={nut()}>
											Payout lock…
										</button>
									)}
								</td>
								<td style={{ ...cell, display: "flex", gap: 6 }}>
									{onOpenStations ? (
										<button type="button" onClick={() => onOpenStations(c)} style={nut(true)}>
											Stations →
										</button>
									) : null}
									<button type="button" disabled={busy === c.id} onClick={() => save(c)} style={nut()}>
										{busy === c.id ? "Saving…" : "Save name"}
									</button>
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</div>
	);
};

export default ChainsTable;
